import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="analytics--drill-down"
export default class extends Controller {
  static targets = [
    "modal",
    "title",
    "content",
    "loading",
    "error",
    "backdrop",
    "closeButton",
    "breadcrumb",
    "breadcrumbCurrent",
  ];
  static values = {
    type: String,
    filters: Object,
    drillDownUrl: String,
    currentData: Object,
  };

  connect() {
    this.setupEventListeners();
    this.setupKeyboardHandling();
  }

  disconnect() {
    this.cleanup();
  }

  // Set up event listeners for drill-down requests
  setupEventListeners() {
    this.element.addEventListener(
      "analytics:drillDownRequested",
      this.handleDrillDownRequest.bind(this),
    );

    // Listen for clicks outside modal to close
    if (this.hasBackdropTarget) {
      this.backdropTarget.addEventListener(
        "click",
        this.handleBackdropClick.bind(this),
      );
    }
  }

  // Set up keyboard handling for accessibility
  setupKeyboardHandling() {
    document.addEventListener("keydown", this.handleKeyDown.bind(this));
  }

  // Clean up event listeners
  cleanup() {
    this.element.removeEventListener(
      "analytics:drillDownRequested",
      this.handleDrillDownRequest.bind(this),
    );
    document.removeEventListener("keydown", this.handleKeyDown.bind(this));

    if (this.hasBackdropTarget) {
      this.backdropTarget.removeEventListener(
        "click",
        this.handleBackdropClick.bind(this),
      );
    }
  }

  // Handle drill-down requests from other controllers
  handleDrillDownRequest(event) {
    const { type, filters, title, section, value } = event.detail;

    this.typeValue = type;
    this.filtersValue = filters || {};

    const drillDownTitle = title || this.getDefaultTitle(type);
    this.updateTitle(drillDownTitle);
    this.updateBreadcrumb(type, drillDownTitle, section);
    this.show();
    this.loadData();
  }

  // Handle backdrop clicks to close modal
  handleBackdropClick(event) {
    if (event.target === this.backdropTarget) {
      this.close();
    }
  }

  // Handle keyboard events
  handleKeyDown(event) {
    if (!this.isModalVisible()) return;

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        this.close();
        break;
      case "Tab":
        this.handleTabNavigation(event);
        break;
    }
  }

  // Handle tab navigation within modal
  handleTabNavigation(event) {
    const focusableElements = this.getFocusableElements();
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  // Get focusable elements within modal
  getFocusableElements() {
    const focusableSelectors = [
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "a[href]",
      '[tabindex]:not([tabindex="-1"])',
    ];

    return Array.from(
      this.modalTarget.querySelectorAll(focusableSelectors.join(", ")),
    );
  }

  // Show drill-down modal
  show(event) {
    if (event) {
      event.preventDefault();
    }

    if (!this.hasModalTarget) {
      console.error("Modal target not found");
      return;
    }

    // Show modal with animation
    this.modalTarget.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");

    // Trigger animation
    requestAnimationFrame(() => {
      this.modalTarget.classList.add("opacity-100");
      this.modalTarget.classList.remove("opacity-0");

      const modalContent = this.modalTarget.querySelector(
        "[data-modal-content]",
      );
      if (modalContent) {
        modalContent.classList.add("scale-100");
        modalContent.classList.remove("scale-95");
      }
    });

    // Focus management
    this.setInitialFocus();

    // Dispatch modal opened event
    this.dispatchModalEvent("opened");
  }

  // Hide drill-down modal
  hide(event) {
    if (event) {
      event.preventDefault();
    }

    if (!this.hasModalTarget) return;

    // Animate out
    this.modalTarget.classList.add("opacity-0");
    this.modalTarget.classList.remove("opacity-100");

    const modalContent = this.modalTarget.querySelector("[data-modal-content]");
    if (modalContent) {
      modalContent.classList.add("scale-95");
      modalContent.classList.remove("scale-100");
    }

    // Hide after animation
    setTimeout(() => {
      this.modalTarget.classList.add("hidden");
      document.body.classList.remove("overflow-hidden");
      this.clearContent();
    }, 200);

    // Dispatch modal closed event
    this.dispatchModalEvent("closed");
  }

  // Close modal (alias for hide)
  close(event) {
    this.hide(event);
  }

  // Load drill-down data via AJAX
  async loadData() {
    if (!this.hasDrillDownUrlValue) {
      console.error("No drill-down URL provided");
      this.showError("Configuration error: No data URL provided");
      return;
    }

    try {
      this.showLoading();
      this.hideError();

      const params = new URLSearchParams({
        type: this.typeValue,
        ...this.filtersValue,
      });

      const response = await fetch(`${this.drillDownUrlValue}?${params}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": this.getCSRFToken(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.currentDataValue = data;
      this.updateContent(data);
    } catch (error) {
      console.error("Failed to load drill-down data:", error);
      this.showError("Failed to load data. Please try again.");
    } finally {
      this.hideLoading();
    }
  }

  // Update modal content with loaded data
  updateContent(data) {
    if (!this.hasContentTarget) return;

    // Update breadcrumbs if provided by server
    if (data.breadcrumbs) {
      this.updateBreadcrumb(
        this.typeValue,
        this.titleTarget?.textContent || "Details",
        null,
        data.breadcrumbs,
      );
    }

    if (data.html) {
      // Server-rendered HTML content
      this.contentTarget.innerHTML = data.html;
    } else if (data.parties) {
      // JSON data - render party list
      this.renderPartyList(data);
    } else if (data.items) {
      // Generic items list
      this.renderItemsList(data);
    } else {
      this.showError("No data available");
    }

    // Initialize any nested Stimulus controllers
    this.initializeNestedControllers();
  }

  // Render party list from JSON data
  renderPartyList(data) {
    const parties = data.parties || [];
    const aggregatedMetrics = data.aggregated_metrics || {};

    let html = '<div class="drill-down-content">';

    // Show aggregated metrics if available
    if (Object.keys(aggregatedMetrics).length > 0) {
      html += '<div class="mb-6 p-4 bg-gray-50 rounded-lg">';
      html += '<h3 class="text-lg font-semibold mb-2">Summary</h3>';
      html += '<div class="grid grid-cols-2 md:grid-cols-4 gap-4">';

      Object.entries(aggregatedMetrics).forEach(([key, value]) => {
        const label = this.formatMetricLabel(key);
        const formattedValue = this.formatMetricValue(key, value);
        html += `
          <div class="text-center">
            <div class="text-2xl font-bold text-blue-600">${formattedValue}</div>
            <div class="text-sm text-gray-600">${label}</div>
          </div>
        `;
      });

      html += "</div></div>";
    }

    // Show party list
    if (parties.length > 0) {
      html += '<div class="space-y-4">';

      parties.forEach((party) => {
        html += this.renderPartyCard(party);
      });

      html += "</div>";
    } else {
      html +=
        '<div class="text-center py-8 text-gray-500">No parties found matching the selected criteria.</div>';
    }

    html += "</div>";

    this.contentTarget.innerHTML = html;
  }

  // Render individual party card
  renderPartyCard(party) {
    const statusColor = this.getStatusColor(party.status);
    const completionPercentage = Math.round((party.completion_rate || 0) * 100);

    return `
      <div class="border rounded-lg p-4 hover:shadow-pop transition-shadow">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-2">
              <h4 class="font-semibold text-lg">${party.title || "Untitled Party"}</h4>
              <span class="px-2 py-1 text-xs rounded-full ${statusColor}">${party.status || "draft"}</span>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
              <div>
                <span class="font-medium">Date:</span>
                ${party.event_date ? new Date(party.event_date).toLocaleDateString() : "Not set"}
              </div>
              <div>
                <span class="font-medium">Guests:</span>
                ${party.rsvp_count || 0}/${party.guest_count || 0}
              </div>
              <div>
                <span class="font-medium">Budget:</span>
                ${this.formatCurrency(party.budget_total || 0)}
              </div>
              <div>
                <span class="font-medium">Spent:</span>
                ${this.formatCurrency(party.actual_spending || 0)}
              </div>
            </div>

            <div class="flex items-center gap-4">
              <div class="flex-1">
                <div class="flex items-center justify-between text-sm mb-1">
                  <span>Completion</span>
                  <span>${completionPercentage}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-blue-600 h-2 rounded-full" style="width: ${completionPercentage}%"></div>
                </div>
              </div>

              ${
                party.overdue_tasks > 0
                  ? `
                <div class="text-red-600 text-sm font-medium">
                  ${party.overdue_tasks} overdue
                </div>
              `
                  : ""
              }
            </div>
          </div>

          <div class="ml-4 flex flex-col gap-2">
            <a href="/parties/${party.id}" class="btn btn-sm btn-primary">View Party</a>
            ${party.venue ? `<div class="text-xs text-gray-500">${party.venue}</div>` : ""}
          </div>
        </div>
      </div>
    `;
  }

  // Render generic items list
  renderItemsList(data) {
    const items = data.items || [];

    let html = '<div class="drill-down-content">';

    if (items.length > 0) {
      html += '<div class="space-y-2">';

      items.forEach((item) => {
        html += `
          <div class="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
            <div>
              <div class="font-medium">${item.title || item.name}</div>
              ${item.description ? `<div class="text-sm text-gray-600">${item.description}</div>` : ""}
            </div>
            <div class="text-right">
              ${item.value ? `<div class="font-semibold">${item.value}</div>` : ""}
              ${item.link ? `<a href="${item.link}" class="text-blue-600 text-sm hover:underline">View</a>` : ""}
            </div>
          </div>
        `;
      });

      html += "</div>";
    } else {
      html +=
        '<div class="text-center py-8 text-gray-500">No items found.</div>';
    }

    html += "</div>";

    this.contentTarget.innerHTML = html;
  }

  // Initialize nested Stimulus controllers
  initializeNestedControllers() {
    // Trigger Stimulus to scan for new controllers in the updated content
    if (window.Stimulus && this.contentTarget) {
      window.Stimulus.start();
    }
  }

  // Update modal title
  updateTitle(title) {
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = title;
    }
  }

  // Update breadcrumb navigation
  updateBreadcrumb(type, title, section, serverBreadcrumbs = null) {
    if (!this.hasBreadcrumbTarget) return;

    let breadcrumbItems;

    // Use server-provided breadcrumbs if available
    if (serverBreadcrumbs && Array.isArray(serverBreadcrumbs)) {
      breadcrumbItems = serverBreadcrumbs;
    } else {
      // Fallback to client-side breadcrumb generation
      breadcrumbItems = [
        {
          label: "Dashboard",
          url: "/dashboard",
          icon: "chart-line.svg",
        },
        {
          label: "Analytics Overview",
          url: "/dashboard",
          icon: "grid-round-2-plus.svg",
        },
      ];

      // Add section-specific breadcrumb
      if (section) {
        breadcrumbItems.push({
          label: this.getSectionLabel(section),
          icon: this.getSectionIcon(section),
        });
      }

      // Add current drill-down
      breadcrumbItems.push({
        label: title,
        icon: "magnifying-glass.svg",
      });
    }

    this.renderBreadcrumb(breadcrumbItems);
  }

  // Render breadcrumb HTML
  renderBreadcrumb(items) {
    if (!this.hasBreadcrumbTarget) return;

    let html =
      '<div class="flex items-center space-x-2 text-sm text-on-surface-alt">';

    items.forEach((item, index) => {
      if (index > 0) {
        html += '<span class="text-outline">';
        html +=
          '<svg class="h-3 w-3 fill-current" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" /></svg>';
        html += "</span>";
      }

      if (item.url && index < items.length - 1) {
        html += `<a href="${item.url}" class="text-primary hover:text-primary-hover transition-colors font-medium">`;
        if (item.icon) {
          html += `<svg class="h-4 w-4 inline mr-1 fill-current" aria-hidden="true"><use href="#${item.icon}"></use></svg>`;
        }
        html += `${item.label}</a>`;
      } else {
        html +=
          '<span class="text-on-surface font-medium" aria-current="page">';
        if (item.icon) {
          html += `<svg class="h-4 w-4 inline mr-1 fill-current" aria-hidden="true"><use href="#${item.icon}"></use></svg>`;
        }
        html += `${item.label}</span>`;
      }
    });

    html += "</div>";

    // Update the breadcrumb current target if it exists
    const breadcrumbCurrent = this.element.querySelector(
      '[data-analytics--drill-down-target="breadcrumbCurrent"]',
    );
    if (breadcrumbCurrent && items.length > 0) {
      const lastItem = items[items.length - 1];
      breadcrumbCurrent.textContent = lastItem.label;
    }

    this.breadcrumbTarget.innerHTML = html;
  }

  // Get section label for breadcrumb
  getSectionLabel(section) {
    const labels = {
      party_overview: "Party Overview",
      guest_engagement: "Guest Engagement",
      financial_insights: "Financial Insights",
      timeline_tasks: "Timeline & Tasks",
      product_vendor: "Products & Vendors",
    };

    return (
      labels[section] ||
      section.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  }

  // Get section icon for breadcrumb
  getSectionIcon(section) {
    const icons = {
      party_overview: "calendar.svg",
      guest_engagement: "users.svg",
      financial_insights: "dollar-sign.svg",
      timeline_tasks: "clock.svg",
      product_vendor: "shopping-cart.svg",
    };

    return icons[section] || "folder.svg";
  }

  // Get default title based on drill-down type
  getDefaultTitle(type) {
    const titles = {
      party_status: "Parties by Status",
      financial_category: "Spending by Category",
      guest_engagement: "Guest Engagement",
      timeline_efficiency: "Task Completion",
      product_performance: "Product Performance",
    };

    return titles[type] || "Details";
  }

  // Clear modal content
  clearContent() {
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = "";
    }
    this.currentDataValue = {};
  }

  // Show loading state
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
    }

    if (this.hasContentTarget) {
      this.contentTarget.style.opacity = "0.5";
    }
  }

  // Hide loading state
  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
    }

    if (this.hasContentTarget) {
      this.contentTarget.style.opacity = "1";
    }
  }

  // Show error state
  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }

    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = `
        <div class="text-center py-8">
          <div class="text-red-600 mb-2">⚠️</div>
          <div class="text-gray-600">${message}</div>
          <button class="mt-4 btn btn-sm btn-secondary" data-action="click->analytics--drill-down#loadData">
            Try Again
          </button>
        </div>
      `;
    }
  }

  // Hide error state
  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }
  }

  // Set initial focus when modal opens
  setInitialFocus() {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  // Check if modal is currently visible
  isModalVisible() {
    return (
      this.hasModalTarget && !this.modalTarget.classList.contains("hidden")
    );
  }

  // Dispatch modal events
  dispatchModalEvent(eventType) {
    const event = new CustomEvent(
      `analytics:modal${eventType.charAt(0).toUpperCase() + eventType.slice(1)}`,
      {
        detail: {
          type: this.typeValue,
          filters: this.filtersValue,
          data: this.currentDataValue,
        },
        bubbles: true,
      },
    );

    this.element.dispatchEvent(event);
  }

  // Get CSRF token for AJAX requests
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.getAttribute("content") : "";
  }

  // Format metric labels for display
  formatMetricLabel(key) {
    const labels = {
      total_guests: "Total Guests",
      total_budget: "Total Budget",
      avg_completion_rate: "Avg Completion",
      total_parties: "Total Parties",
    };

    return (
      labels[key] ||
      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  }

  // Format metric values for display
  formatMetricValue(key, value) {
    if (key.includes("budget") || key.includes("spending")) {
      return this.formatCurrency(value);
    } else if (key.includes("rate") || key.includes("percentage")) {
      return `${Math.round(value * 100)}%`;
    } else {
      return value.toLocaleString();
    }
  }

  // Get status color classes
  getStatusColor(status) {
    const colors = {
      draft: "bg-gray-100 text-gray-800",
      in_progress: "bg-blue-100 text-blue-800",
      confirmed: "bg-green-100 text-green-800",
      completed: "bg-purple-100 text-purple-800",
      cancelled: "bg-red-100 text-red-800",
    };

    return colors[status] || colors.draft;
  }

  // Format currency values
  formatCurrency(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  // Refresh current data
  refresh() {
    if (this.isModalVisible()) {
      this.loadData();
    }
  }

  // Export current drill-down data
  exportData() {
    if (
      !this.currentDataValue ||
      Object.keys(this.currentDataValue).length === 0
    ) {
      return;
    }

    const blob = new Blob([JSON.stringify(this.currentDataValue, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `drill-down-${this.typeValue}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
